/**
 * "Select Organization" button + popover for pages that need an active org.
 *
 * Super admins get every organization with a View-as button (an account with no
 * membership has no org to auto-select, so this is the only way in); members get
 * their own memberships to switch to.
 */
import { useEffect, useState } from 'react';
import { collection, getDocs } from 'firebase/firestore';
import { Building2 } from 'lucide-react';
import { db } from '@/config/firebase';
import { useAuth } from '@/contexts/AuthContext';
import { Button, Spinner } from '@/components/ui';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/Popover';
import { ViewAsOrgButton } from '@/components/admin/ViewAsOrgButton';
import type { Organization } from '@/types/auth';

/** Every organization, each with a View-as button. Super admins only. */
export function OrgPickerList({ onSelect }: { onSelect: () => void }) {
  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const orgsRef = collection(db, 'organizations');
    getDocs(orgsRef)
      .then((snapshot) => {
        const data = snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })) as Organization[];
        setOrgs(data.sort((a, b) => a.name.localeCompare(b.name)));
      })
      .finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-3"><Spinner size="sm" /></div>;

  return (
    <>
      {orgs.map((org) => (
        <div key={org.id} className="flex items-center justify-between gap-2 px-1 py-0.5">
          <span className="text-sm truncate flex-1 px-1">{org.name}</span>
          <ViewAsOrgButton
            organizationId={org.id}
            organizationName={org.name}
            onSuccess={onSelect}
            compact
          />
        </div>
      ))}
    </>
  );
}

export function SelectOrganizationButton({
  className,
  superAdminLabel = 'View as:',
}: {
  className?: string;
  superAdminLabel?: string;
}) {
  const { isSuperAdmin, organizations, switchOrganization } = useAuth();
  const [open, setOpen] = useState(false);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" className={className ?? 'flex items-center gap-2'}>
          <Building2 className="w-4 h-4" />
          Select Organization
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" collisionPadding={16} className="w-64 p-2 max-h-80 overflow-y-auto">
        <p className="text-xs text-muted-foreground px-2 pb-2">
          {isSuperAdmin ? superAdminLabel : 'Switch to:'}
        </p>
        {isSuperAdmin ? (
          <OrgPickerList onSelect={() => setOpen(false)} />
        ) : organizations.length === 0 ? (
          <p className="px-2 py-1.5 text-sm text-muted-foreground">
            You are not a member of any organization yet. Ask an admin to invite you.
          </p>
        ) : (
          organizations.map((membership) => (
            <button
              key={membership.organizationId}
              onClick={() => { switchOrganization(membership.organizationId); setOpen(false); }}
              className="w-full flex items-center gap-2 px-2 py-1.5 text-sm rounded hover:bg-muted text-left"
            >
              <Building2 className="w-4 h-4 text-muted-foreground" />
              {membership.organizationName}
            </button>
          ))
        )}
      </PopoverContent>
    </Popover>
  );
}
